import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { MessageSquare, Send, Trash2, Loader2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
}

/**
 * NodeComments — a lightweight, embeddable comment thread for any platform node.
 * Scoped per (tenant, node_id) with an optional property_id filter — comments made
 * against the "overall" scope appear on every property view; property-specific ones
 * only on that property.
 */
export default function NodeComments({ token, nodeId, propertyId, isAdmin }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const [expanded, setExpanded] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!nodeId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND}/api/nodes/${nodeId}/comments`, {
        headers,
        params: propertyId ? { property_id: propertyId } : {},
      });
      setComments(r.data.comments || []);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [nodeId, propertyId, headers]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await axios.post(`${BACKEND}/api/nodes/${nodeId}/comments`,
        { text: t, property_id: propertyId || 'overall' },
        { headers });
      setComments(prev => [r.data, ...prev]);
      setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not post');
    } finally { setSending(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${BACKEND}/api/nodes/${nodeId}/comments/${id}`, { headers });
      setComments(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  if (!nodeId) return null;

  return (
    <div data-testid="node-comments">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
          data-testid="node-comments-toggle"
        >
          <MessageSquare className="h-3 w-3" /> Comments
          {comments.length > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-bold ml-1">{comments.length}</span>
          )}
        </button>
      </div>

      {expanded && (
        <>
          <form onSubmit={submit} className="flex items-start gap-1.5 mb-2" data-testid="node-comment-form">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e); }}
              placeholder="Add a note — ⌘↵ to send. Visible to your team."
              rows={2}
              className="flex-1 text-xs px-2 py-1.5 rounded-md border bg-background resize-none focus:outline-none focus:ring-1 focus:ring-ring"
              maxLength={2000}
              data-testid="node-comment-input"
            />
            <Button type="submit" size="icon" className="h-8 w-8 shrink-0" disabled={sending || !text.trim()} data-testid="node-comment-submit">
              {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            </Button>
          </form>

          {loading && comments.length === 0 ? (
            <div className="py-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>
          ) : comments.length === 0 ? (
            <p className="text-[11px] text-muted-foreground italic py-2">Be the first to leave a note on this node.</p>
          ) : (
            <div className="space-y-1.5" data-testid="node-comments-list">
              {comments.map(c => {
                // Backend enforces the actual permission — this just shows the button
                // on hover for admins (and everyone sees the graceful 403 otherwise)
                const canDelete = isAdmin || true;
                return (
                  <div key={c.id} className="group text-xs p-2 rounded-md bg-muted/40 border border-transparent hover:border-border transition-colors" data-testid={`node-comment-${c.id}`}>
                    <div className="flex items-baseline justify-between gap-2 mb-0.5">
                      <div className="font-semibold text-foreground truncate">
                        {c.user_name || c.user_email || 'Anonymous'}
                        {c.property_id && c.property_id !== 'overall' && (
                          <span className="ml-1 text-[9px] font-normal text-blue-600 dark:text-blue-400">· this property only</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[9px] text-muted-foreground">{timeAgo(c.created_at)}</span>
                        {canDelete && (
                          <button
                            onClick={() => remove(c.id)}
                            className="opacity-0 group-hover:opacity-100 h-4 w-4 rounded flex items-center justify-center text-muted-foreground hover:text-destructive transition-opacity"
                            data-testid={`node-comment-delete-${c.id}`}
                            title="Delete"
                          >
                            <Trash2 className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-foreground whitespace-pre-wrap break-words leading-snug">{c.text}</p>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
